import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { translateFoodQuery, isCyrillic } from "@/i18n/foodDictionary";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Coffee, Sun, Sunset, Cookie, Flame, Trash2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { trackMealLogged } from "@/utils/analytics";
import { MealSection, calcMealDIL } from "./meal-log/MealSection";
import { FoodSearchOverlay } from "./meal-log/FoodSearchOverlay";
import { computeDil } from "@/lib/dilFormula";
import DailyDiaryNote from "./DailyDiaryNote";
import CollapsiblePanel from "./CollapsiblePanel";
import RecipeVisionUpload from "./RecipeVisionUpload";
import MealLogVoiceButton from "./meal-log/MealLogVoiceButton";
import NISHistoryChart from "./diary/NISHistoryChart";
import FormulaVersionBadge from "./FormulaVersionBadge";
import { useDashboardMode } from "@/dashboard/DashboardModeContext";  // iter566 — mode-aware labels
import { DIL_TERM } from "@/utils/dilTerminology";  // iter566
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor, KeyboardSensor,
  useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";

const MEAL_TYPES = [
  { id: "breakfast", icon: Coffee, labelBg: "Закуска", labelEn: "Breakfast", color: "#F59E0B" },
  { id: "lunch", icon: Sun, labelBg: "Обяд", labelEn: "Lunch", color: "#0EA5E9" },
  { id: "dinner", icon: Sunset, labelBg: "Вечеря", labelEn: "Dinner", color: "#6366F1" },
  { id: "snack", icon: Cookie, labelBg: "Междинни", labelEn: "Snacks", color: "#10B981" },
];

export default function MealLog({ onMealsUpdate, variant = "desktop", selectedDate = null, openSignal = 0 }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const { mode: dashMode } = useDashboardMode();  // iter566 — "easy" | "standard" | "expert"
  const bg = language === "bg";
  const [meals, setMeals] = useState({ breakfast: [], lunch: [], dinner: [], snack: [] });
  const [totals, setTotals] = useState({ calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, saturated_fat: 0 });
  const [effectiveDil, setEffectiveDil] = useState({});
  const [dailyEffDil, setDailyEffDil] = useState(null);
  const [dailyRawDil, setDailyRawDil] = useState(null);
  const [nisByMeal, setNisByMeal] = useState({});   // iter376
  const [nisDaily, setNisDaily] = useState(null);   // iter376
  const [mdiDaily, setMdiDaily] = useState(null);   // iter960
  const [mdiTier, setMdiTier]   = useState(null);   // iter960
  const [satietyDaily, setSatietyDaily] = useState(null);  // iter960
  const [satietyTier, setSatietyTier]   = useState(null);  // iter960
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeMealType, setActiveMealType] = useState("breakfast");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);
  const [amount, setAmount] = useState("100");
  const [adding, setAdding] = useState(false);
  const [translatedQuery, setTranslatedQuery] = useState("");
  // iter327N — optional consumption time (HH:MM) for circadian DIL
  const [logTime, setLogTime] = useState(() => {
    const d = new Date();
    return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
  });

  const fetchMeals = useCallback(async () => {
    try {
      const url = selectedDate ? `${API}/meals/today?date=${selectedDate}` : `${API}/meals/today`;
      const { data } = await axios.get(url);
      setMeals(data.meals);
      setTotals(data.totals);
      setEffectiveDil(data.effective_dil || {});
      setDailyEffDil(data.daily_effective_dil ?? null);
      setDailyRawDil(data.daily_raw_dil ?? null);
      setNisByMeal(data.nis_by_meal || {});       // iter376
      setNisDaily(data.nis_daily || null);        // iter376
      // iter960 — Daily MDI + SI aggregates
      setMdiDaily(data.mdi_daily ?? null);
      setMdiTier(data.mdi_daily_tier || null);
      setSatietyDaily(data.satiety_daily ?? null);
      setSatietyTier(data.satiety_daily_tier || null);
    } catch (e) {
      // meals fetch silently fails
    }
  }, [API, selectedDate]);

  useEffect(() => { fetchMeals(); }, [fetchMeals]);

  // Listen for meal-added / meallog:refresh events (from RecipeExplorer, MealPlanReview)
  useEffect(() => {
    const handler = () => fetchMeals();
    window.addEventListener("meal-added", handler);
    window.addEventListener("meallog:refresh", handler);
    window.addEventListener("meal-log-changed", handler);
    // iter222 — Plate Simulator "+ Добави храна" button opens the same MealLog
    // food search overlay so user can add food without leaving Diary.
    const focusHandler = (e) => {
      try {
        const mealType = e?.detail?.mealType;
        if (mealType) setActiveMealType(mealType);
        else {
          const h = new Date().getHours();
          setActiveMealType(h < 11 ? "breakfast" : h < 15 ? "lunch" : h < 19 ? "snack" : "dinner");
        }
        if (e?.detail?.query) setSearchQuery(e.detail.query);
        setSearchOpen(true);
      } catch { /* ignore */ }
    };
    window.addEventListener("meal-log:focus-search", focusHandler);
    return () => {
      window.removeEventListener("meal-added", handler);
      window.removeEventListener("meallog:refresh", handler);
      window.removeEventListener("meal-log-changed", handler);
      window.removeEventListener("meal-log:focus-search", focusHandler);
    };
  }, [fetchMeals]);

  const searchFood = async (queryOverride) => {
    const q = typeof queryOverride === "string" ? queryOverride : searchQuery;
    if (!q.trim() || q.length < 2) return;
    setSearching(true);
    setSelectedFood(null);

    let apiQuery = q;
    let translated = "";

    if (isCyrillic(q)) {
      const translatedQ = translateFoodQuery(q);
      if (translatedQ !== q) {
        translated = translatedQ;
      }
      apiQuery = q;
    }
    setTranslatedQuery(translated);

    try {
      const { data } = await axios.get(`${API}/food/search`, { params: { q: apiQuery } });
      let results = data.products || [];

      if (isCyrillic(q) && translated && results.length < 5) {
        try {
          const { data: enData } = await axios.get(`${API}/food/search`, { params: { q: translated } });
          const existingNames = new Set(results.map(r => r.name.toLowerCase()));
          const extraResults = (enData.products || []).filter(p => !existingNames.has(p.name.toLowerCase()));
          results = [...results, ...extraResults];
        } catch (e) { /* ignore */ }
      }

      // Also search custom foods
      try {
        const { data: customData } = await axios.get(`${API}/food/custom/search`, { params: { q: apiQuery } });
        const customResults = (customData.foods || []).map(f => ({
          ...f, isCustom: true, brand: "Custom"
        }));
        results = [...customResults, ...results];
      } catch (e) { /* custom foods not available yet */ }

      // Sort: supplements first, then local DB, then USDA/external
      // iter327Q — supplements should appear first for queries like "витамин"
      results.sort((a, b) => {
        const getPriority = (item) => {
          if (item.source === "supplements") return 0;  // Supplements first
          if (item.source === "local" || item.isCustom) return 1;  // Local/custom second
          return 2;  // USDA/external last
        };
        return getPriority(a) - getPriority(b);
      });

      setSearchResults(results);
    } catch (e) {
      toast.error(bg ? "Грешка при търсене" : "Search error");
    } finally {
      setSearching(false);
    }
  };

  const addFoodToMeal = async () => {
    if (!selectedFood) return;
    setAdding(true);
    const grams = parseFloat(amount) || 100;
    const ratio = grams / 100;
    const p = selectedFood.per100g;

    try {
      const { data } = await axios.post(`${API}/meals/add`, {
        meal_type: activeMealType,
        food_name: selectedFood.name,
        amount_g: grams,
        calories: Math.round(p.calories * ratio * 10) / 10,
        protein: Math.round(p.protein * ratio * 10) / 10,
        carbs: Math.round(p.carbs * ratio * 10) / 10,
        fat: Math.round(p.fat * ratio * 10) / 10,
        fiber: Math.round((p.fiber || 0) * ratio * 10) / 10,
        sugar: Math.round((p.sugar || 0) * ratio * 10) / 10,
        saturated_fat: Math.round((p.saturated_fat || 0) * ratio * 10) / 10,
        fdc_id: selectedFood.fdc_id || null,
        source: selectedFood.source || null,
        ...(selectedFood.supplement_info ? { supplement_info: selectedFood.supplement_info } : {}),
        ...(selectedDate ? { log_date: selectedDate } : {}),
        ...(logTime ? { log_time: logTime } : {}),
      });
      await fetchMeals();
      onMealsUpdate?.();
      const addedFoodSource = selectedFood.source || "local";
      setSelectedFood(null);
      setSearchQuery("");
      setSearchResults([]);
      setSearchOpen(false);
      toast.success(bg ? "Храната е добавена!" : "Food added!");
      // Notify other widgets (Hero card, usage, insights) to re-fetch.
      window.dispatchEvent(new Event("meal-log-changed"));

      // ── Insulin response indication (Nutramilo v2.0 scale) ──
      // Computes insulin density (% kcal from insulin-stimulating nutrients)
      // and shows personalized warning if user has known metabolic phenotype.
      const addedCal = Math.round(p.calories * ratio * 10) / 10;
      const addedProt = Math.round(p.protein * ratio * 10) / 10;
      const addedCarbs = Math.round(p.carbs * ratio * 10) / 10;
      const addedFiber = Math.round((p.fiber || 0) * ratio * 10) / 10;
      // DIL v2.0 — see https://pypi.org/project/nutramilo-nis/
      const foodDil = computeDil(addedProt, addedCarbs, addedFiber);
      // GA4 conversion event — value = DIL of this food entry. Calculated after
      // foodDil is available; uses the food source captured before reset.
      trackMealLogged(foodDil, activeMealType, addedFoodSource);
      // iter523 — "Инсулинова плътност" warning REMOVED.
      // Reason: (DIL × 4 / kcal × 100) inflates warning for low-calorie foods
      // like blueberries (DIL 3.2, ~28 kcal → 45-80% "density") even though
      // their actual insulin load is trivial. NIS (Nutramilo Insulin Score v1.1.9)
      // remains the canonical predicted-insulin metric and is shown in the
      // Plate Simulator. Per-food warnings should rely on NIS, not on a
      // synthetic ratio that lacks an underlying physiologic anchor.
      // Celebrate backfill streak bonus when granted
      if (data?.backfill_bonus?.granted) {
        const msg = bg
          ? `🎉 +${data.backfill_bonus.credits} кредита за попълване на дневника!`
          : `🎉 +${data.backfill_bonus.credits} credits for catching up your diary!`;
        toast.success(msg, { duration: 6000 });
      }
    } catch (e) {
      toast.error(bg ? "Грешка при добавяне" : "Error adding food");
    } finally {
      setAdding(false);
    }
  };

  const deleteMeal = async (mealId) => {
    try {
      await axios.delete(`${API}/meals/${mealId}`);
      await fetchMeals();
      onMealsUpdate?.();
      // iter221b — keep Plate Simulator (and other widgets) in sync after delete.
      window.dispatchEvent(new Event("meal-log-changed"));
    } catch (e) {
      toast.error(bg ? "Грешка при изтриване" : "Error deleting");
    }
  };

  const editMeal = async (mealId, updates) => {
    try {
      await axios.put(`${API}/meals/${mealId}`, updates);
      await fetchMeals();
      onMealsUpdate?.();
      window.dispatchEvent(new Event("meal-log-changed"));
      toast.success(bg ? "Порцията е обновена!" : "Portion updated!");
    } catch (e) {
      toast.error(bg ? "Грешка при редактиране" : "Error editing");
    }
  };

  const [dragItem, setDragItem] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const findEntry = (id) => {
    for (const [mt, list] of Object.entries(meals)) {
      const found = list.find(e => e.id === id);
      if (found) return { entry: found, fromMeal: mt };
    }
    return null;
  };

  const handleDndStart = (event) => {
    const found = findEntry(event.active.id);
    if (found) setDragItem(found);
  };

  const handleDndEnd = async (event) => {
    const { active, over } = event;
    setDragItem(null);
    if (!over) return;
    const dragged = findEntry(active.id);
    if (!dragged) return;
    const toMealType = String(over.id).startsWith("meal-zone-") ? String(over.id).replace("meal-zone-", "") : null;
    if (!toMealType || toMealType === dragged.fromMeal) return;
    // Optimistic UI — move immediately
    setMeals(prev => {
      const next = { ...prev };
      next[dragged.fromMeal] = (next[dragged.fromMeal] || []).filter(e => e.id !== dragged.entry.id);
      next[toMealType] = [...(next[toMealType] || []), dragged.entry];
      return next;
    });
    try {
      await axios.put(`${API}/meals/${dragged.entry.id}/move`, { new_meal_type: toMealType });
      await fetchMeals();
      onMealsUpdate?.();
      toast.success(bg ? "Храната е преместена!" : "Food moved!");
    } catch (e) {
      await fetchMeals();
      toast.error(bg ? "Грешка при преместване" : "Error moving");
    }
  };

  const openSearchForMeal = (mealType) => {
    setActiveMealType(mealType);
    setSearchOpen(true);
    setSearchQuery("");
    setSearchResults([]);
    setSelectedFood(null);
    setAmount("100");
    setTranslatedQuery("");
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchResults([]);
    setSelectedFood(null);
    // iter327Z — notify Dashboard so it can restore the scroll position to the
    // caller (e.g. Plate Simulator's "Add food" button).
    try { window.dispatchEvent(new Event("meal-log:search-closed")); } catch {}
  };

  const addCustomFood = async (customData) => {
    try {
      await axios.post(`${API}/food/custom`, customData);
      toast.success(bg ? "Custom храна запазена!" : "Custom food saved!");
      // Auto-select it for adding to meal
      setSelectedFood({
        name: customData.name,
        brand: "Custom",
        isCustom: true,
        per100g: {
          calories: parseFloat(customData.calories) || 0,
          protein: parseFloat(customData.protein) || 0,
          carbs: parseFloat(customData.carbs) || 0,
          fat: parseFloat(customData.fat) || 0,
          fiber: parseFloat(customData.fiber) || 0,
          sugar: parseFloat(customData.sugar) || 0,
          saturated_fat: parseFloat(customData.saturated_fat) || 0,
        }
      });
      setAmount("100");
    } catch (e) {
      toast.error(bg ? "Грешка при запазване" : "Error saving custom food");
      throw e;
    }
  };

  const activeMeal = MEAL_TYPES.find(m => m.id === activeMealType);

  const showEffective = dailyEffDil != null && dailyEffDil > 0;
  const showRaw = dailyRawDil != null && dailyRawDil > 0;
  const isBuffered = showEffective && showRaw && dailyEffDil < dailyRawDil;
  const allEntriesForFallback = Object.values(meals).flat();
  const fallbackDIL = calcMealDIL(allEntriesForFallback);
  const dilTerm = DIL_TERM[dashMode] || DIL_TERM.easy;  // iter566 — mode-aware
  const dilLabel = dashMode === "easy" ? "Товар" : "DIL";
  const formatDilValue = (v) => {
    const n = typeof v === "number" ? v : parseFloat(v);
    const safe = Number.isFinite(n) ? n : 0;
    if (dashMode === "expert") return `${safe.toFixed(1)} g`;
    if (dashMode === "standard") return `${safe.toFixed(1)}`;
    return `${Math.round(safe)} ${dilTerm.unit}`;
  };
  const dilPill = (
    <>
      {showEffective ? (
        <span className={`font-bold px-1.5 py-0.5 rounded text-[10px] ${isBuffered ? (isDark ? 'bg-green-900/20 text-green-400' : 'bg-green-50 text-green-600') : (isDark ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-500')}`} data-testid="daily-dil-total">
          {dilLabel} {formatDilValue(dailyEffDil)}
          {isBuffered && <span className={`ml-1 text-[8px] line-through opacity-50 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{formatDilValue(dailyRawDil)}</span>}
        </span>
      ) : fallbackDIL != null ? (
        <span className={`font-bold px-1.5 py-0.5 rounded text-[10px] ${isDark ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-500'}`} data-testid="daily-dil-total">
          {dilLabel} {formatDilValue(fallbackDIL)}
        </span>
      ) : null}
    </>
  );

  const today = new Date().toISOString().slice(0, 10);
  const isPastDate = selectedDate && selectedDate !== today;
  const dateLabel = isPastDate ? (() => {
    try {
      const d = new Date(selectedDate);
      return d.toLocaleDateString(bg ? "bg-BG" : "en-US", { day: "numeric", month: "short" });
    } catch { return selectedDate; }
  })() : null;
  const subtitle = [
    dateLabel ? `📅 ${dateLabel}` : null,
    `${Math.round(totals.calories)} kcal · ${Math.round(totals.protein)}g P · ${Math.round(totals.carbs)}g C · ${Math.round(totals.fat)}g F · ${Math.round(totals.fiber || 0)}g Fi`,
  ].filter(Boolean).join(" · ");

  const handleClearDate = async () => {
    if (!isPastDate) return;
    const entriesCount = Object.values(meals).reduce((sum, list) => sum + (list?.length || 0), 0);
    if (entriesCount === 0) {
      toast.info(bg ? "Няма записи за изтриване" : "No entries to clear");
      return;
    }
    const confirmed = window.confirm(bg
      ? `Сигурни ли сте, че искате да изтриете ВСИЧКИ ${entriesCount} записа за ${dateLabel}? Това действие е необратимо.`
      : `Are you sure you want to delete ALL ${entriesCount} entries for ${dateLabel}? This cannot be undone.`);
    if (!confirmed) return;
    try {
      const { data } = await axios.delete(`${API}/meals/clear-date/${selectedDate}`);
      toast.success(bg ? `Изтрити ${data.deleted} записа за ${dateLabel}` : `Deleted ${data.deleted} entries for ${dateLabel}`);
      await fetchMeals();
      onMealsUpdate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка при изтриване" : "Failed to clear"));
    }
  };

  const clearDateBadge = isPastDate ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); handleClearDate(); }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 transition-colors"
      data-testid="meal-log-clear-date-btn"
      title={bg ? "Изтрий всички записи за този ден" : "Clear all entries for this day"}
    >
      <Trash2 size={10} />
      {bg ? "Изчисти деня" : "Clear day"}
    </button>
  ) : null;

  const draggedEntry = dragItem?.entry;

  return (
    <>
      <div id="meal-log-anchor" style={{ scrollMarginTop: 20 }} />
      <CollapsiblePanel
        title={bg ? "Хранителен дневник" : "Meal Log"}
        subtitle={subtitle}
        icon={Flame}
        iconColor="#F59E0B"
        credits={0}
        badge={clearDateBadge || dilPill}
        storageKey={`meal-log-${variant}`}
        testId={`meal-log-card${variant === "mobile" ? "-m" : ""}`}
        openSignal={openSignal}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDndStart}
          onDragEnd={handleDndEnd}
          onDragCancel={() => setDragItem(null)}
        >
          <div className="space-y-3">
            {/* Voice + photo entry-points side by side */}
            <div className="flex items-center justify-end gap-2 flex-wrap">
              <MealLogVoiceButton />
              <RecipeVisionUpload variant="meal-log" onAdded={() => fetchMeals()} />
            </div>
            {MEAL_TYPES.map((mealType) => (
              <MealSection
                key={mealType.id}
                mealType={mealType}
                entries={meals[mealType.id] || []}
                effectiveDilData={effectiveDil[mealType.id] || null}
                isDark={isDark}
                bg={bg}
                language={language}
                variant={variant}
                onOpenSearch={openSearchForMeal}
                onDeleteMeal={deleteMeal}
                onEditMeal={editMeal}
              />
            ))}
            {/* iter376 — NIS history chart (only when at least one meal logged) */}
            {nisDaily && (
              <NISHistoryChart
                nisByMeal={nisByMeal}
                nisDaily={nisDaily}
                lang={language}
                isDark={isDark}
              />
            )}
            {/* iter960 — Daily MDI + SI aggregate strip */}
            {(mdiDaily != null || satietyDaily != null) && (
              <div
                className={`flex items-center justify-center gap-4 rounded-lg px-3 py-2 border ${isDark ? "bg-slate-900/40 border-slate-800" : "bg-slate-50 border-slate-200"}`}
                data-testid="meallog-mdi-si-daily-strip"
              >
                {mdiDaily != null && (
                  <div className="flex items-center gap-1.5" data-testid="meallog-mdi-daily">
                    <span
                      className="w-2.5 h-2.5 rounded-full inline-block"
                      style={{
                        background: mdiDaily >= 60 ? "#10B981" : mdiDaily >= 30 ? "#F59E0B" : "#9CA3AF",
                      }}
                    />
                    <span className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      {bg ? "MDI (плътност)" : "MDI (density)"}
                    </span>
                    <span className="text-sm font-bold tabular-nums">{Number(mdiDaily).toFixed(0)}</span>
                    <span className={`text-[9px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                      {mdiTier ? (bg
                        ? { "Nutrient-dense": "плътна", "Moderate": "умерена", "Sparse": "рядка" }[mdiTier] || mdiTier
                        : mdiTier) : ""}
                    </span>
                  </div>
                )}
                {satietyDaily != null && (
                  <div className="flex items-center gap-1.5" data-testid="meallog-si-daily">
                    <span
                      className="w-2.5 h-2.5 rounded-full inline-block"
                      style={{
                        background: satietyDaily >= 130 ? "#10B981" : satietyDaily >= 80 ? "#F59E0B" : "#EF4444",
                      }}
                    />
                    <span className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      {bg ? "SI (засищане)" : "SI (satiety)"}
                    </span>
                    <span className="text-sm font-bold tabular-nums">{Number(satietyDaily).toFixed(0)}</span>
                    <span className={`text-[9px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                      {satietyTier ? (bg
                        ? { "Very filling": "много засищаща", "Moderate": "умерена", "Weak satiety": "слаба" }[satietyTier] || satietyTier
                        : satietyTier) : ""}
                    </span>
                  </div>
                )}
              </div>
            )}
            <DailyDiaryNote />
            {/* iter410 — EU AI Act Art. 50 transparency: показва активни версии на формулите */}
            <div className="flex justify-end pt-1">
              <FormulaVersionBadge />
            </div>
          </div>
          <DragOverlay dropAnimation={{ duration: 200 }}>
            {draggedEntry ? (
              <div className={`rounded-lg shadow-2xl px-3 py-2 text-xs font-medium ring-2 ring-[#0EA5E9] ${isDark ? 'bg-[hsl(223,47%,14%)] text-white' : 'bg-white text-[#1A1A2E]'}`}>
                {draggedEntry.food_name} · {Math.round(draggedEntry.calories)} kcal
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </CollapsiblePanel>

      {/* Full-Screen Food Search Overlay */}
      {searchOpen && (
        <FoodSearchOverlay
          isDark={isDark} bg={bg} language={language}
          activeMeal={activeMeal}
          activeMealType={activeMealType}
          setActiveMealType={setActiveMealType}
          mealTypes={MEAL_TYPES}
          searchQuery={searchQuery} setSearchQuery={setSearchQuery}
          onSearch={searchFood}
          searching={searching} searchResults={searchResults}
          translatedQuery={translatedQuery}
          selectedFood={selectedFood} setSelectedFood={setSelectedFood}
          amount={amount} setAmount={setAmount}
          adding={adding} onAddFood={addFoodToMeal}
          onClose={closeSearch}
          onAddCustomFood={addCustomFood}
          logTime={logTime}
          setLogTime={setLogTime}
        />
      )}
    </>
  );
}
